/**
 * What may leave the browser inside a Sentry event (#112).
 *
 * This is a money app. CLAUDE.md rule 7 keeps amounts, names, masks and user
 * text out of analytics; Sentry is a second third party and gets the same
 * boundary, enforced in `beforeSend` rather than trusted to the SDK defaults.
 *
 * **Stance: strip by default, keep an allow-list of what is safe.** Error
 * messages are the hard case — `DataGrid`'s and `Money`'s diagnostics carry
 * amounts (see `ErrorBoundary.componentDidCatch`) and nothing can tell a
 * merchant name inside a sentence from a word. So exception *messages* are
 * replaced wholesale and the exception TYPE plus the stack stay; the stack
 * names the function and line, which is what diagnoses a crash.
 *
 * URLs keep origin and path shape, but a path segment that is an id or a token
 * becomes `:id` / `:token`, and the query string and fragment are dropped
 * (Firebase action links carry `oobCode`/`apiKey` there).
 *
 * Structural types only, so this file imports nothing from the SDK and the
 * tests feed it plain objects.
 */

export const REDACTED = '[redacted]'

/**
 * Routes whose NEXT segment is a secret or a person's text, whatever it looks
 * like: `/verify/:token`, `/invite/:token`, and `/cashflow/payer/:name` — a
 * payer name (`Netflix`, `Priya`) is free text and no shape test can tell it
 * from a route word. Position decides.
 */
const SECRET_PARENTS = new Set(['verify', 'invite', 'reset', 'join', 'accept', 'confirm', 'payer'])

/**
 * Every parameterised route in the app, and what its parameter holds. A guard
 * (`sentryScrub.test.ts`) scans the source for route patterns and fails when one
 * is missing from here, so a new name-bearing route cannot ship unclassified.
 * `id` = a backend id (UUID-shaped, caught by shape); `secret` = a token;
 * `name` = free text that only position can catch (see SECRET_PARENTS).
 */
export const ROUTE_PARAM_POLICY: Readonly<Record<string, 'id' | 'secret' | 'name'>> = {
  '/verify/:token': 'secret',
  '/invite/:token': 'secret',
  '/categories/:id': 'id',
  '/rules/:id': 'id',
  '/accounts/:id': 'id',
  '/transactions/:id': 'id',
  '/merchants/:id/transactions': 'id',
  '/cashflow/:scope/:id?': 'name',
  '/connections/reconnect/:itemId': 'id',
  '/recurring/:id': 'id',
  '/recurring/:id/edit': 'id',
  '/budget/:id': 'id',
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A segment that is data rather than a route name: all digits, a UUID, long, or
 * mixed letters-and-digits (`acc_8f3a21`, `tx9912`). Route words in this app are
 * plain lower-case words, so none of them matches.
 */
function isIdLike(segment: string): boolean {
  if (/^\d+$/.test(segment)) return true
  if (UUID.test(segment)) return true
  if (segment.length >= 20) return true
  return /\d/.test(segment) && /[a-z]/i.test(segment) && segment.length >= 6
}

export function scrubPath(pathname: string): string {
  const segments = pathname.split('/')
  let previous = ''
  const out = segments.map((segment) => {
    const lower = segment.toLowerCase()
    let next = segment
    if (segment !== '') {
      if (SECRET_PARENTS.has(previous)) next = ':token'
      else if (isIdLike(segment)) next = ':id'
    }
    previous = lower
    return next
  })
  return out.join('/')
}

/**
 * Scrub one URL, absolute or relative. Anything unparseable becomes REDACTED —
 * never passed through, because a string we cannot read is a string we cannot
 * vouch for.
 */
export function scrubUrl(raw: string): string {
  try {
    const parsed = new URL(raw, 'http://placeholder.invalid')
    const relative = !/^[a-z][a-z0-9+.-]*:/i.test(raw) && !raw.startsWith('//')
    const path = scrubPath(parsed.pathname)
    return relative ? path : `${parsed.origin}${path}`
  } catch {
    return REDACTED
  }
}

const URL_IN_TEXT = /https?:\/\/[^\s"'<>)]+/gi
/** A bare app path inside text (`GET /cashflow/payer/Priya?q=1`): starts the text or follows a space. */
const PATH_IN_TEXT = /(^|\s)(\/[^\s"'<>)]*)/g

/** Free text that may embed URLs (span descriptions): scrub each URL in place. */
export function scrubUrlsInText(text: string): string {
  return text
    .replace(URL_IN_TEXT, (match) => scrubUrl(match))
    .replace(PATH_IN_TEXT, (_m, lead: string, path: string) => `${lead}${scrubUrl(path)}`)
}

type Dict = Record<string, unknown>

type SentryishEvent = {
  message?: string
  transaction?: string
  user?: Dict
  extra?: Dict
  request?: {
    url?: string
    cookies?: unknown
    headers?: Record<string, string>
    data?: unknown
    query_string?: unknown
    [key: string]: unknown
  }
  exception?: { values?: Array<ExceptionValue> }
  breadcrumbs?: Breadcrumbish[]
  contexts?: unknown
  spans?: Array<{ description?: string; data?: Dict; [key: string]: unknown }>
  [key: string]: unknown
}

type Frame = { abs_path?: string; filename?: string; vars?: unknown; [key: string]: unknown }
type ExceptionValue = {
  value?: string
  mechanism?: { data?: unknown; [key: string]: unknown }
  stacktrace?: { frames?: Frame[]; [key: string]: unknown }
  [key: string]: unknown
}

/** `contexts` is an ALLOW-list: only these survive, and `trace` is cut to its ids. */
const SAFE_CONTEXTS = ['browser', 'os', 'runtime', 'device'] as const
const SAFE_TRACE_KEYS = ['trace_id', 'span_id', 'parent_span_id', 'op', 'status', 'origin']

function scrubContexts(contexts: Dict): Dict {
  const out: Dict = {}
  for (const key of SAFE_CONTEXTS) if (key in contexts) out[key] = contexts[key]
  const trace = contexts.trace
  if (trace && typeof trace === 'object') {
    const safe: Dict = {}
    for (const key of SAFE_TRACE_KEYS) if (key in trace) safe[key] = (trace as Dict)[key]
    out.trace = safe
  }
  return out
}

const stripQuery = (path: string): string => path.split(/[?#]/)[0] ?? ''

function scrubFrame(frame: Frame): Frame {
  const { vars: _vars, ...rest } = frame
  const next: Frame = rest
  // Bundle paths carry a query string (`?v=hash`) at best, a token at worst.
  // Cut at `?`/`#` rather than parse: a frame path may use a scheme `URL` calls
  // opaque (`app:///`), and only the suffix is at issue.
  if (typeof next.abs_path === 'string') next.abs_path = stripQuery(next.abs_path)
  if (typeof next.filename === 'string') next.filename = stripQuery(next.filename)
  return next
}

function scrubException(v: ExceptionValue): ExceptionValue {
  const { mechanism, stacktrace, ...rest } = v
  const next: ExceptionValue = { ...rest, ...(v.value === undefined ? {} : { value: REDACTED }) }
  if (mechanism) {
    const { data: _data, ...m } = mechanism
    next.mechanism = m
  }
  if (stacktrace) {
    next.stacktrace = stacktrace.frames
      ? { ...stacktrace, frames: stacktrace.frames.map(scrubFrame) }
      : stacktrace
  }
  return next
}

export type Breadcrumbish = {
  category?: string
  message?: string
  data?: Dict
  [key: string]: unknown
}

/** Only these request headers survive. Referer is kept, scrubbed — it is a URL. */
function scrubHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [name, value] of Object.entries(headers)) {
    const lower = name.toLowerCase()
    if (lower === 'user-agent') out[name] = value
    else if (lower === 'referer') out[name] = scrubUrl(value)
  }
  return out
}

/**
 * Breadcrumb filter. `null` drops it.
 *
 * Dropped outright: console (log text is arbitrary), `ui.click`/`ui.input`
 * (their message is a DOM selector that can embed labels or values). Kept and
 * scrubbed: fetch/xhr (method + status, URL without query) and navigation.
 * Anything else is dropped — an unknown category is unvetted.
 */
export function scrubBreadcrumb<T extends object>(input: T): T | null {
  const crumb = input as unknown as Breadcrumbish
  const category = crumb.category ?? ''
  if (category === 'fetch' || category === 'xhr') {
    const data = crumb.data ?? {}
    const safe: Dict = {}
    for (const key of ['method', 'status_code']) {
      if (key in data) safe[key] = data[key]
    }
    if (typeof data.url === 'string') safe.url = scrubUrl(data.url)
    return { ...crumb, message: undefined, data: safe } as unknown as T
  }
  if (category === 'navigation') {
    const data = crumb.data ?? {}
    const safe: Dict = {}
    for (const key of ['from', 'to']) {
      const v = data[key]
      if (typeof v === 'string') safe[key] = scrubUrl(v)
    }
    return { ...crumb, message: undefined, data: safe } as unknown as T
  }
  return null
}

/**
 * The `beforeSend` / `beforeSendTransaction` body. Mutates nothing: returns a
 * scrubbed copy, so a test (and a reader) can compare before and after.
 *
 * The user is whatever the caller put there, reduced to `id` — which is the
 * HASHED analytics id by construction (`setAnalyticsUser`), never an email.
 */
export function scrubEvent<T extends object>(input: T): T {
  const out: SentryishEvent = { ...(input as unknown as SentryishEvent) }

  if (out.request) {
    const { url, headers } = out.request
    // cookies, data (the request body), query_string: all dropped by omission.
    const request: NonNullable<SentryishEvent['request']> = {}
    if (typeof url === 'string') request.url = scrubUrl(url)
    if (headers) request.headers = scrubHeaders(headers)
    out.request = request
  }

  if (out.user) {
    const id = out.user.id
    out.user = typeof id === 'string' ? { id } : {}
  }

  delete out.extra
  // `tags` and `logentry` are free-form (a tag value is whatever a caller set;
  // `logentry.message`/`params` is a formatted message), so neither is vetted.
  delete out.tags
  delete out.logentry
  if (out.contexts && typeof out.contexts === 'object') out.contexts = scrubContexts(out.contexts as Dict)
  if (typeof out.message === 'string') out.message = REDACTED
  if (typeof out.transaction === 'string') out.transaction = scrubUrl(out.transaction)

  if (out.exception?.values) {
    out.exception = { ...out.exception, values: out.exception.values.map(scrubException) }
  }

  if (out.breadcrumbs) {
    out.breadcrumbs = out.breadcrumbs
      .map((b) => scrubBreadcrumb(b))
      .filter((b): b is Breadcrumbish => b !== null)
  }

  if (out.spans) {
    out.spans = out.spans.map((span) => {
      const next = { ...span }
      if (typeof next.description === 'string') next.description = scrubUrlsInText(next.description)
      // Span data carries `http.query`, full urls and request bodies' sizes.
      delete next.data
      return next
    })
  }

  return out as unknown as T
}
